"use client";

import { ArrowRight, Flame, Wind, Mountain, Snowflake, Brain, Waves, Feather, Sun, Candy } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const classes = [{
    code: "§ S.01",
    icon: Flame,
    title: "COMMERCIAL HVAC",
    description: "High-performance climate control systems engineered for large-scale office environments and retail spaces.",
    duration: "FULL INSTALL"
  }, {
    code: "§ S.02",
    icon: Wind,
    title: "RESIDENTIAL COOLING",
    description: "Bespoke air conditioning solutions designed to keep your home comfortable throughout the summer months.",
    duration: "CUSTOM FIT"
  }, {
    code: "§ S.03",
    icon: Mountain,
    title: "SYSTEM UPGRADES",
    description: "Modernizing outdated infrastructure with energy-efficient units that reduce operational costs significantly.",
    duration: "EFFICIENCY"
  }, {
    code: "§ S.04",
    icon: Snowflake,
    title: "PREVENTATIVE CARE",
    description: "Comprehensive maintenance schedules to ensure your equipment runs at peak performance year-round.",
    duration: "MAINTENANCE"
  }, {
    code: "§ S.05",
    icon: Brain,
    title: "SMART CONTROLS",
    description: "Integration of intelligent climate management systems for precise temperature control and monitoring.",
    duration: "AUTOMATION"
  }, {
    code: "§ S.06",
    icon: Waves,
    title: "AIR PURIFICATION",
    description: "Advanced filtration technology to improve indoor air quality and remove allergens from your environment.",
    duration: "AIR QUALITY"
  }, {
    code: "§ S.07",
    icon: Feather,
    title: "EMERGENCY REPAIR",
    description: "Rapid response service for critical system failures to restore your comfort without unnecessary delay.",
    duration: "24/7 SUPPORT"
  }, {
    code: "§ S.08",
    icon: Sun,
    title: "ENERGY AUDITS",
    description: "Detailed analysis of your current cooling setup to identify potential savings and performance gaps.",
    duration: "CONSULTING"
  }, {
    code: "§ S.09",
    icon: Candy,
    title: "SYSTEM DESIGN",
    description: "Professional planning and layout services for new builds and complex renovation projects.",
    duration: "ENGINEERING"
  }];
  return <section data-section-id="1291" id="horshamprofessionalservices" className="bg-background text-foreground py-24 md:py-44 px-6 md:px-12 relative overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute top-20 right-0 md:right-12 select-none font-sans leading-none text-foreground/[0.04]" style={{
      fontSize: "clamp(18rem, 32vw, 36rem)"
    }}>
        01
      </div>

      <div className="max-w-[1440px] mx-auto relative">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-[520ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-10">
              <span className="font-sans text-xs md:text-sm uppercase tracking-[0.18em] text-foreground font-medium">
                § 01 / OUR EXPERTISE
              </span>
              <span className="block w-10 h-px bg-foreground" />
            </div>
            <h2 className="font-default uppercase text-foreground leading-[0.86]" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            letterSpacing: "-0.015em"
          }}>
              CLIMATE{" "}
              <span className="font-default italic text-primary" style={{
              letterSpacing: "0.01em"
            }}>
                SOLUTIONS
              </span>
            </h2>
          </div>

          {}
          <div className="lg:col-span-8 lg:pt-4 flex flex-col justify-end">
            <p className="font-default text-muted-foreground max-w-2xl mb-8" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}>
              Coastline Air Conditioning Services provides premium air conditioning and climate control services across Sussex and Surrey. From commercial installations to residential maintenance, we ensure your environment remains perfectly tempered.
            </p>
            <div className="flex items-center gap-3">
              <span className="block w-2 h-2 bg-primary" />
              <span className="font-sans text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium">
                — 09 SERVICES · PROFESSIONAL STANDARDS
              </span>
            </div>
          </div>
        </div>

        {}
        <div className="relative mb-16 md:mb-20">
          <div className="h-px w-full bg-border" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {classes.map((cls, i) => <ClassCard key={cls.title} data-index={i} index={i} code={cls.code} Icon={cls.icon} title={cls.title} description={cls.description} duration={cls.duration} />)}
        </div>
      </div>
    </section>;
}
function ClassCard({
  index,
  code,
  Icon,
  title,
  description,
  duration,
  ...rest
}: {
  index: number;
  code: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  duration: string;
  "data-index"?: number;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>();
  const delay = index % 3 * 80 + Math.floor(index / 3) * 40;
  return <div {...rest} ref={reveal.ref} className={`group relative bg-background p-8 md:p-10 transition-all duration-[520ms] ease-out hover:bg-card ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: reveal.isVisible ? `${delay}ms` : "0ms"
  }}>
      {}
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-primary transition-all duration-300 ease-out" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-primary transition-all duration-300 ease-out" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-primary transition-all duration-300 ease-out group-hover:w-[20px] group-hover:h-[1.5px]" style={{
        width: "12px",
        height: "1.5px"
      }} />
      </span>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-primary transition-all duration-300 ease-out group-hover:w-[1.5px] group-hover:h-[20px]" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>

      {}
      <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 transition-all duration-300 ease-out group-hover:bottom-2 group-hover:right-2">
        <span className="block bg-primary transition-all duration-300 ease-out group-hover:w-[20px] group-hover:h-[1.5px] ml-auto" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-primary transition-all duration-300 ease-out group-hover:w-[1.5px] group-hover:h-[20px] ml-auto" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>

      {}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 border border-border transition-all duration-300 ease-out group-hover:border-[2.5px] group-hover:border-primary" />

      <div className="relative flex flex-col h-full min-h-[340px]">
        {}
        <div className="mb-8">
          <Icon className="text-foreground transition-colors duration-300 ease-out group-hover:text-primary" strokeWidth={1.5} />
        </div>

        {}
        <div className="mb-4">
          <span className="font-sans text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">
            {code}
          </span>
        </div>

        {}
        <h3 className="font-default uppercase text-foreground mb-5" style={{
        fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
        lineHeight: 0.92,
        letterSpacing: "0em"
      }}>
          {title}
        </h3>

        {}
        <p className="text-muted-foreground mb-8 flex-grow" style={{
        fontFamily: "var(--font-sans)",
        fontSize: "0.9375rem",
        lineHeight: 1.65
      }}>
          {description}
        </p>

        {}
        <div className="flex items-end justify-between mt-auto pt-6 border-t border-border">
          <Link to="/AirConditioningPage" className="relative inline-flex items-center gap-2 group/link text-primary hover:text-primary font-sans uppercase tracking-[0.04em] text-sm">
            <span className="relative">
              LEARN MORE
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[220ms] ease-out group-hover/link:w-full" />
            </span>
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <span className="font-sans text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium">
            {duration}
          </span>
        </div>
      </div>
    </div>;
}